/**
 * Care Planner home — the caseload, at the root of
 * care-planner.<tenant>.
 *
 * adr/0023: an app opens on a SUBJECT, so this lists the subjects there are —
 * the open stays the nurse may see — and opening one goes to `/stays/{id}`.
 *
 * THERE IS NO "NEW PLAN". This page used to list care-planner SESSIONS and mint
 * one per plan, which is what made the plan live inside a conversation. A stay
 * has a care plan the moment anyone adds an intervention to it, and an empty
 * plan is an answer, so there is nothing to create: the board opens on the stay
 * either way.
 *
 * Design (DESIGN.md): flat hairline header, token surfaces, always-on
 * ReturnToMainAgent on the left edge.
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'

import { MainAppNav } from '@frontend/components/MainAppNav'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
import { Skeleton } from '@frontend/components/ui/skeleton'
import { useLocations } from '@frontend/components/admin/org/useOrg'
import { useCaseload } from '@frontend/hooks/useEncounters'
import { usePatients, usePatientsConfig } from '@frontend/hooks/usePatients'
import { formatPatientLabel } from '@frontend/lib/patientLabel'

export default function CarePlannerHome() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: stays, isLoading, error } = useCaseload()
  const { data: patients } = usePatients()
  const { data: locations } = useLocations()
  const labelFormat = usePatientsConfig()?.label_format

  // Patient identity is the deployment's own label format over the real
  // record — never a name, and never anything an agent wrote.
  const labelFor = useMemo(() => {
    const byToken = new Map((patients ?? []).map((p) => [p.token, p]))
    return (patientId: string) =>
      formatPatientLabel(byToken.get(patientId)?.attributes ?? {}, labelFormat, patientId)
  }, [patients, labelFormat])

  const unitFor = useMemo(() => {
    const byId = new Map((locations ?? []).map((l) => [l.id, l.name]))
    // An id with no name is a location the caller cannot read. Showing the raw
    // id says less than showing nothing, so the row simply carries no unit.
    return (locationId: string | null) => (locationId ? (byId.get(locationId) ?? '') : '')
  }, [locations])

  return (
    <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      <header className="flex items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <ReturnToMainAgent className="-ml-2" />
        <h1 className="min-w-0 flex-1 truncate text-[19px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)]">
          {t('carePlan.homeTitle', 'Care plans')}
        </h1>
        <MainAppNav />
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 overflow-auto px-4 py-4">
        {error ? (
          <div className="px-6 py-16 text-center">
            <p className="text-[17px] text-[var(--color-status-danger,#b3261e)]">
              {t('carePlan.caseloadFailed', 'The caseload could not be read.')}
            </p>
          </div>
        ) : isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-[60px] w-full rounded-[18px]" />
            <Skeleton className="h-[60px] w-full rounded-[18px]" />
            <Skeleton className="h-[60px] w-full rounded-[18px]" />
          </div>
        ) : (stays ?? []).length === 0 ? (
          <div className="mx-auto flex max-w-md flex-col items-center gap-2 px-6 py-16 text-center">
            <p className="text-[17px] text-[var(--color-surface-mute)]">
              {t('carePlan.emptyCaseload', 'No patients are admitted.')}
            </p>
            <p className="text-[14px] text-[var(--color-surface-mute)]">
              {t(
                'carePlan.emptyCaseloadHint',
                'A care plan belongs to a stay. Admit a patient to plan her week.',
              )}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="care-planner-stay-list">
            {(stays ?? []).map((stay) => (
              <li key={stay.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/stays/${stay.id}`)}
                  className="flex min-h-11 w-full items-center gap-3 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-5 py-3 text-left transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[17px] font-semibold text-[var(--color-surface-ink)]">
                      {labelFor(stay.patient)}
                    </p>
                    <p className="truncate text-[14px] text-[var(--color-surface-mute)]">
                      {unitFor(stay.location)}
                    </p>
                  </div>
                  <ChevronRight
                    className="h-5 w-5 shrink-0 text-[var(--color-surface-mute)]"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  )
}
